import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase/client';
import { clearAuthData } from '../utils/auth';

interface AuthCheckProps {
  children: React.ReactNode;
}

export default function AuthCheck({ children }: AuthCheckProps) {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let isNavigating = false;
    
    const checkAuth = () => {
      
      // Simple check: if we have an access token, allow access
      // Token validation will happen when making API calls to the server
      const accessToken = localStorage.getItem('accessToken');
      
      if (!accessToken) {
        if (mounted && !isNavigating) {
          isNavigating = true;
          navigate('/login', { replace: true });
          setIsLoading(false);
        }
        return;
      }

      if (mounted) {
        setIsAuthenticated(true);
        setIsLoading(false);
      }
      
      // Note: Token validation happens on the server when making API calls
      // If the token is invalid/expired, the server will return 401 and redirect to login
    };

    checkAuth();
    
    // Listen for logout event
    const handleLogout = () => {
      if (mounted && !isNavigating) {
        isNavigating = true;
        setIsAuthenticated(false);
        setIsLoading(false);
        navigate('/login', { replace: true });
      }
    };
    
    window.addEventListener('userLoggedOut', handleLogout);

    // Listen for auth state changes - with better error handling
    let subscription: any = null;
    
    try {
      const authListener = supabase.auth.onAuthStateChange(async (event, session) => {
        
        if (!mounted || isNavigating) return;
        
        if (event === 'SIGNED_OUT') {
          isNavigating = true;
          setIsAuthenticated(false);
          await clearAuthData();
          navigate('/login', { replace: true });
        } else if (event === 'TOKEN_REFRESHED') {
          if (session?.access_token) {
            localStorage.setItem('accessToken', session.access_token);
          }
        } else if (event === 'SIGNED_IN') {
          setIsAuthenticated(true);
          if (session?.access_token) {
            localStorage.setItem('accessToken', session.access_token);
          }
        } else if (!session && event !== 'INITIAL_SESSION') {
          isNavigating = true;
          setIsAuthenticated(false);
          await clearAuthData();
          navigate('/login', { replace: true });
        }
      });
      
      subscription = authListener.data.subscription;
    } catch (error) {
      // Continue without listener - auth check already passed
    }

    return () => {
      mounted = false;
      window.removeEventListener('userLoggedOut', handleLogout);
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}